import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  IconButton,
  Card,
  CardContent,
  Grid,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';

const PaymentEditRequests = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [adminComments, setAdminComments] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/payment-edit-requests/pending');
      setRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching payment edit requests:', error);
      showToast('Failed to load payment edit requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setActionType('approve');
    setAdminComments('');
    setActionDialogOpen(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setActionType('reject');
    setAdminComments('');
    setActionDialogOpen(true);
  };

  const submitAction = async () => {
    if (!selectedRequest) return;

    try {
      const endpoint = actionType === 'approve'
        ? `/admin/payment-edit-requests/${selectedRequest.id}/approve`
        : `/admin/payment-edit-requests/${selectedRequest.id}/reject`;

      await api.post(endpoint, null, {
        params: {
          adminId: user.userId,
          adminComments: adminComments || undefined,
        },
      });

      showToast(
        `Payment edit request ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`,
        'success'
      );

      setActionDialogOpen(false);
      setSelectedRequest(null);
      setAdminComments('');
      fetchRequests();
    } catch (error) {
      console.error(`Error ${actionType}ing request:`, error);
      showToast(
        error.response?.data?.error || `Failed to ${actionType} request`,
        'error'
      );
    }
  };

  const formatCurrency = (amount) => {
    const num = Number(amount || 0);
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-LK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'COMPLETED':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#0D47A1',
            mb: 1,
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
          }}
        >
          Payment Edit Requests
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Review and manage payment edit requests from Field Officers
        </Typography>
      </Box>

      {requests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Pending Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            There are no pending payment edit requests at this time.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'primary.main' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Request ID</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Loan ID</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Inst. #</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Current Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Requested Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Requested By</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Requested At</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>#{request.id}</TableCell>
                  <TableCell>
                    {request.installment?.loan?.customer?.fullName || 
                     request.installment?.loan?.customer?.nic || 
                     'N/A'}
                  </TableCell>
                  <TableCell>#{request.installment?.loan?.id || 'N/A'}</TableCell>
                  <TableCell>{request.installment?.installmentNumber || 'N/A'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(request.currentPaymentAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {formatCurrency(request.requestedPaymentAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {request.requestedBy?.fullName || 'N/A'}
                  </TableCell>
                  <TableCell>{formatDate(request.requestedAt)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDetails(request)}
                        sx={{ textTransform: 'none' }}
                      >
                        View
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleApprove(request)}
                        sx={{ textTransform: 'none' }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={() => handleReject(request)}
                        sx={{ textTransform: 'none' }}
                      >
                        Reject
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedRequest(null);
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1' }}>
              Payment Edit Request Details
            </Typography>
            <IconButton onClick={() => {
              setDetailDialogOpen(false);
              setSelectedRequest(null);
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Request ID
                      </Typography>
                      <Typography variant="h6">#{selectedRequest.id}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Status
                      </Typography>
                      <Chip
                        label={selectedRequest.status}
                        color={getStatusColor(selectedRequest.status)}
                        size="small"
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Current Payment Amount
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatCurrency(selectedRequest.currentPaymentAmount)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Requested Payment Amount
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {formatCurrency(selectedRequest.requestedPaymentAmount)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Customer Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Customer Name
                      </Typography>
                      <Typography variant="body1">
                        {selectedRequest.installment?.loan?.customer?.fullName || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        NIC
                      </Typography>
                      <Typography variant="body1">
                        {selectedRequest.installment?.loan?.customer?.nic || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Loan Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Loan ID
                      </Typography>
                      <Typography variant="body1">#{selectedRequest.installment?.loan?.id || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Installment Number
                      </Typography>
                      <Typography variant="body1">
                        {selectedRequest.installment?.installmentNumber || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Request Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Requested By
                      </Typography>
                      <Typography variant="body1">
                        {selectedRequest.requestedBy?.fullName || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Requested At
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedRequest.requestedAt)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Reason for Edit
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="body2">
                          {selectedRequest.reason || 'No reason provided'}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDetailDialogOpen(false);
            setSelectedRequest(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog (Approve/Reject) */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => {
          setActionDialogOpen(false);
          setSelectedRequest(null);
          setAdminComments('');
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1' }}>
              {actionType === 'approve' ? 'Approve' : 'Reject'} Payment Edit Request
            </Typography>
            <IconButton onClick={() => {
              setActionDialogOpen(false);
              setSelectedRequest(null);
              setAdminComments('');
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Alert
                severity={actionType === 'approve' ? 'info' : 'warning'}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  {actionType === 'approve'
                    ? 'Are you sure you want to approve this payment edit request? The Field Officer will be able to edit the payment after approval.'
                    : 'Are you sure you want to reject this payment edit request? The request will be marked as rejected and the Field Officer will be notified.'}
                </Typography>
              </Alert>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Current Amount
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(selectedRequest.currentPaymentAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Requested Amount
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {formatCurrency(selectedRequest.requestedPaymentAmount)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <TextField
                fullWidth
                label="Admin Comments (Optional)"
                multiline
                rows={4}
                value={adminComments}
                onChange={(e) => setAdminComments(e.target.value)}
                placeholder={
                  actionType === 'approve'
                    ? 'Add any comments about this approval...'
                    : 'Provide a reason for rejection...'
                }
                helperText="These comments will be visible to the Field Officer"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 2 }}>
          <Button
            onClick={() => {
              setActionDialogOpen(false);
              setSelectedRequest(null);
              setAdminComments('');
            }}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={submitAction}
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
            sx={{ textTransform: 'none' }}
          >
            {actionType === 'approve' ? 'Approve' : 'Reject'} Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentEditRequests;

